import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Skeleton,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import { colors, fonts, size } from "../../ColorFont/color";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const FAQs = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500); // Simulate loading for 500ms
    return () => clearTimeout(timer);
  }, []);

  const faqs = [
    {
      question: t("question1"),
      answer: t("answer1"),
    },
    {
      question: t("question2"),
      answer: t("answer2"),
    },
    {
      question: t("question3"),
      answer: t("answer3"),
    },
    {
      question: t("question4"),
      answer: t("answer4"),
    },
    {
      question: t("question5"),
      answer: t("answer5"),
    },
    {
      question: t("question6"),
      answer: t("answer6"),
    },
  ];

  return (
    <Box sx={{ margin: "0 auto", maxWidth: "1600px", px: 0 }}>


      <Box
        sx={{
          maxWidth: "1600px",
          mx: "auto",
          p: 0,
          borderRadius: isMobile ? 2 : 3,
        }}
      >
        {/* Intro Section */}
        {loading ? (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
          >
            <Box sx={{ mb: isMobile ? 2 : 3 }}>
              <Skeleton variant="text" width="80%" sx={{ mx: "auto", mb: 1 }} />
              <Skeleton variant="text" width="90%" sx={{ mx: "auto" }} />
              <Skeleton variant="text" width="70%" sx={{ mx: "auto" }} />
            </Box>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
          >
            <Typography
              sx={{
                fontFamily: fonts.poppins,
                fontSize: isMobile ? '13px' : '15px',
                textAlign: "left",
                color: colors.text,
                mb: isMobile ? 2 : 3,
                px: isMobile ? 1 : 0,
              }}
            >
              {t("faqsIntro")}
            </Typography>
          </motion.div>
        )}

        {/* FAQs List */}
        {loading ? (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.5 }}
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.5, 
                  ease: "easeOut", 
                  delay: 0.7 + (index * 0.1) 
                }}
              >
                <Accordion
                  sx={{
                    mb: isMobile ? 2 : 3,
                    borderRadius: "24px",
                    overflow: "hidden",
                    borderLeft: isMobile ? `4px solid #e0e0e0` : `6px solid #e0e0e0`,
                    backgroundColor: "#fff",
                    boxShadow: "0px 1px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#e0e0e0", fontSize: isMobile ? 20 : 24 }} />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                    sx={{
                      fontFamily: fonts.poppins,
                      fontWeight: 600,
                      fontSize: isMobile ? size.smallLabel : "16px",
                      padding: isMobile ? "8px 12px" : "14px",
                      color: colors.dark,
                      minHeight: isMobile ? "48px" : "64px",
                      "& .MuiAccordionSummary-content": {
                        margin: isMobile ? "8px 0" : "12px 0",
                      }
                    }}
                  >
                    <Skeleton variant="text" width="80%" height={isMobile ? 20 : 24} />
                  </AccordionSummary>
                  <AccordionDetails 
                    sx={{ 
                      backgroundColor: "#fff", 
                      p: isMobile ? 1.5 : 2,
                      pt: isMobile ? 0 : 2
                    }}
                  >
                    <Skeleton variant="text" width="95%" height={isMobile ? 16 : 20} />
                    <Skeleton variant="text" width="90%" height={isMobile ? 16 : 20} />
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.5 }}
          >
            {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ 
                duration: 0.5, 
                ease: "easeOut", 
                delay: 0.7 + (index * 0.1) 
              }}
            >
              <Box sx={{ mb: isMobile ? 3 : 4 }}>
                <Accordion
                  elevation={0}
                  disableGutters
                  sx={{
                    borderRadius: "24px !important",
                    overflow: "hidden",
                    border: `1px solid ${colors.background_dark}40`,
                    borderLeft: isMobile ? `4px solid ${colors.primary_dark}` : `6px solid ${colors.primary_dark}`,
                    backgroundColor: "#fff",
                    boxShadow: expanded === `panel${index}` 
                      ? "0px 6px 16px rgba(0,0,0,0.08)" 
                      : "0px 2px 8px rgba(0,0,0,0.04)",
                    "&:before": { display: "none" },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: isMobile ? "none" : "translateY(-2px)",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ 
                        color: colors.primary_dark,
                        fontSize: isMobile ? 22 : 26
                      }} />
                    }
                    sx={{
                      fontFamily: fonts.poppins,
                      fontWeight: 600,
                      fontSize: isMobile ? "14px" : "16px",
                      px: isMobile ? 2 : 3,
                      py: isMobile ? 0.5 : 1,
                      color: colors.dark,
                      "&.Mui-expanded": {
                        backgroundColor: "colors.primary_rgba_4",
                      }
                    }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails 
                    sx={{ 
                      px: isMobile ? 2 : 3,
                      pb: 2,
                      pt: 1,
                      backgroundColor: "colors.primary_rgba_4",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: fonts.poppins,
                        fontSize: isMobile ? "13px" : "15px",
                        color: colors.text,
                        lineHeight: 1.6,
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </motion.div>
            ))}
          </motion.div>
        )}

        {/* Footer Contact Section */}
        <Box sx={{ mt: isMobile ? 5 : 8 }}>
          <Divider sx={{ marginY: isMobile ? 2 : 3 }} />
          {loading ? (
            <Skeleton variant="text" width="80%" sx={{ mx: "auto", mb: 1 }} />
          ) : (
            <Typography 
              sx={{
                fontFamily: fonts.poppins, 
                textAlign: "center", 
                marginTop: isMobile ? 3 : 5, 
                fontSize: isMobile ? "13px" : "15px", 
                color: colors.text,
                backgroundColor: "colors.primary_rgba_5",
                p: 3,
                borderRadius: 3,
                border: `1px dashed ${colors.primary_dark}80`,
              }}
            >
              {t("help")}
              {" "}
              <a
                href="mailto:krushisetuofficial@gmail.com"
                style={{
                  color: colors.primary_dark,
                  fontWeight: 600,
                  textDecoration: "underline",
                  marginLeft: "4px",
                }}
              >
                krushisetuofficial@gmail.com
              </a>
              {" "}
              {t("orCall")}
              {" "}
              <a
                href="tel:+917040180680"
                style={{
                  color: colors.primary_dark,
                  fontWeight: 600,
                  textDecoration: "underline",
                  marginLeft: "4px",
                }}
              >
                +91 7040180680
              </a>
            </Typography>
          )}
        </Box>

        {/* Brand Copyright */}
        <Box sx={{ mt: 3, pb: 2 }}>
          {loading ? (
            <Skeleton variant="text" width="60%" sx={{ mx: "auto" }} />
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                fontFamily: fonts.poppins,
                fontSize: isMobile ? "12px" : "14px",
                color: colors.text,
                opacity: 0.8,
              }}
            >
              {t("footer")}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FAQs;